import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchFulfillmentAging,
  type FulfillmentAgingResponse,
} from '../api/reports';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant, formatRelativeTime } from '../utils/datetime';
import { fulfillmentStatusLabel } from '../utils/orderStatus';

const STATUS_ORDER = [
  'ready',
  'on_hold',
  'fulfillment_queued',
  'label_created',
  'error_retry',
] as const;

const AGE_FORMAT = { maxDays: 3650, fallback: '—' } as const;

export const ReportsFulfillmentAgingPage: React.FC = () => {
  const [report, setReport] = useState<FulfillmentAgingResponse | null>(null);
  const [source, setSource] = useState('');
  const [limit, setLimit] = useState('200');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const buildParams = useCallback(() => {
    const parsedLimit = Number.parseInt(limit, 10);
    return {
      limit: Number.isNaN(parsedLimit) || parsedLimit <= 0 ? 200 : Math.min(parsedLimit, 2000),
      source: source.trim() || undefined,
    };
  }, [limit, source]);

  const loadReport = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const nextReport = await fetchFulfillmentAging(buildParams());
      setReport(nextReport);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fulfillment aging report');
      setReport(null);
    } finally {
      setIsLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    void loadReport();
  }, []);

  const handleApplyFilters = async (event: React.FormEvent) => {
    event.preventDefault();
    await loadReport();
  };

  const isEmpty = report != null && report.count === 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Reports: Fulfillment Aging</h1>
          <p style={styles.subtitle}>
            Paid orders still open for fulfillment, oldest paid first. Tracking is shown when a
            fulfillment job has one. This page only reads orders — it does not create labels or
            contact carriers.
          </p>
        </div>
        <div style={styles.headerLinks}>
          <Link to="/orders/combined" style={styles.linkButton}>Orders</Link>
          <Link to="/reports/shipped-shortfall-with-stock" style={styles.linkButton}>Shipped Shortfall</Link>
        </div>
      </div>

      <form style={styles.filtersRow} onSubmit={handleApplyFilters}>
        <label style={styles.filterLabel}>
          Source
          <input
            value={source}
            onChange={(event) => setSource(event.target.value)}
            style={styles.input}
            placeholder="manapool"
          />
        </label>
        <label style={styles.filterLabel}>
          Limit
          <input
            type="number"
            min={1}
            max={2000}
            value={limit}
            onChange={(event) => setLimit(event.target.value)}
            style={styles.input}
          />
        </label>
        <div style={styles.actions}>
          <button type="submit" style={styles.primaryButton} disabled={isLoading}>
            {isLoading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </form>

      {error && <div style={styles.error}>{error}</div>}

      {isLoading && !report && <p style={styles.metaText}>Loading fulfillment aging…</p>}

      {report && (
        <>
          <div style={styles.summaryGrid}>
            <MetricCard label="Open orders" value={String(report.count)} detail="Paid, not shipped" />
            {STATUS_ORDER.map((status) => (
              <MetricCard
                key={status}
                label={fulfillmentStatusLabel(status)}
                value={String(report.counts[status] ?? 0)}
                detail="Open in this status"
              />
            ))}
          </div>
          <p style={styles.metaText}>
            Generated {formatInstant(report.generated_at)}
            {report.source_filter ? ` · source ${report.source_filter}` : ''}
          </p>

          {isEmpty ? (
            <section role="status" aria-label="Empty fulfillment aging" style={styles.emptyState}>
              <h2 style={styles.sectionTitle}>No open fulfillment orders</h2>
              <p style={styles.emptyCopy}>
                Paid orders in ready, on hold, fulfillment queued, label created, or error retry
                appear here, oldest paid first. An order leaves this list when it is shipped or
                cancelled.
              </p>
            </section>
          ) : (
            <section style={styles.card}>
              <div style={styles.sectionHeader}>
                <h2 style={styles.sectionTitle}>Oldest paid first</h2>
                <span style={styles.metaText}>
                  {report.rows.length < report.count
                    ? `Showing ${report.rows.length} of ${report.count} orders`
                    : `${report.rows.length} order(s)`}
                </span>
              </div>
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Age</th>
                      <th style={styles.th}>Paid</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Channel</th>
                      <th style={styles.th}>Order</th>
                      <th style={styles.th}>Buyer</th>
                      <th style={styles.th}>Tracking</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rows.map((row) => (
                      <tr key={row.order_id}>
                        <td style={styles.tdStrong}>
                          {formatRelativeTime(row.paid_at, AGE_FORMAT)}
                        </td>
                        <td style={styles.td}>{formatInstant(row.paid_at)}</td>
                        <td style={styles.td}>{fulfillmentStatusLabel(row.fulfillment_status)}</td>
                        <td style={styles.td}>{row.source || '—'}</td>
                        <td style={styles.tdMono}>{row.external_id || '—'}</td>
                        <td style={styles.td}>{row.buyer_name || '—'}</td>
                        <td style={styles.tdMono}>{row.tracking_number || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};

function MetricCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: string;
  detail: string;
}) {
  return (
    <div style={styles.metricCard}>
      <span style={styles.metricLabel}>{label}</span>
      <strong style={styles.metricValue}>{value}</strong>
      <span style={styles.metricDetail}>{detail}</span>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.lg,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.md,
    flexWrap: 'wrap',
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    maxWidth: '720px',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  headerLinks: {
    display: 'flex',
    gap: spacing.sm,
    flexWrap: 'wrap',
    alignItems: 'flex-start',
  },
  linkButton: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.surface,
    color: colors.text,
    padding: `${spacing.sm} ${spacing.md}`,
    textDecoration: 'none',
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: spacing.md,
    alignItems: 'end',
    marginBottom: spacing.md,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  filterLabel: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    color: colors.text,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
    minWidth: '160px',
  },
  input: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.sm,
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    color: colors.text,
    backgroundColor: colors.background,
  },
  actions: {
    display: 'flex',
    gap: spacing.sm,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: spacing.md,
    marginBottom: spacing.sm,
  },
  metricCard: {
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  metricLabel: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
  },
  metricValue: {
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  metricDetail: {
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  metaText: {
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    marginBottom: spacing.md,
  },
  card: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.md,
  },
  emptyState: {
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
    padding: spacing.lg,
  },
  emptyCopy: {
    marginTop: spacing.sm,
    marginBottom: 0,
    maxWidth: '640px',
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: spacing.sm,
    flexWrap: 'wrap',
    marginBottom: spacing.md,
  },
  sectionTitle: {
    margin: 0,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: '860px',
  },
  th: {
    textAlign: 'left',
    padding: spacing.sm,
    fontSize: typography.small.fontSize,
    color: colors.textSecondary,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: 'nowrap',
  },
  td: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdStrong: {
    padding: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
  tdMono: {
    padding: spacing.sm,
    fontSize: typography.caption.fontSize,
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    color: colors.textSecondary,
    borderBottom: `1px solid ${colors.border}`,
    verticalAlign: 'top',
  },
};
